import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch, GET_ELECTRONIC_DOCS, RootState, apiInstance } from "shared";
import {
  downloadDocument,
  downloadsDescription,
  downloadsTitle,
} from "shared/utils";
import Swal from "sweetalert2";
import { useDetailedConsultation } from "pages/detailed-consultation/hooks/useDetailedConsultation";
import { ILoadRips } from "../model/ILoadRips";
import {
  RESOLUTION_2275_2023,
  RESOLUTION_3374_2000,
} from "../helper/resolutions";

export const useLoadRips = ({ idDoc, setIsNextPage }: ILoadRips) => {
  const dispatch = useDispatch<Dispatch>();
  const { callGetDetailedDocument } = useDetailedConsultation();
  const { id, originalName, nameNewsRips } = useSelector(
    (state: RootState) => state.linkDocument,
  );
  const { billingState, activityStatus } = useSelector(
    (state: RootState) => state.auditDetails,
  );
  const [resolution3374Document, setResolution3374Document] =
    useState<any>(null);
  const [resolution2275Document, setResolution2275Document] =
    useState<any>(null);
  const [isRipsValidate, setIsRipsValidate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [typeResolutionSet, setTypeResolutionSet] = useState<string>("");
  const [responseRips, setResponseRips] = useState<any>(null);
  const [valueCuv, setValueCuv] = useState<string>("");
  const typeAccept = ["ZIP"];
  const typeAccept2275 = ["JSON"];

  const wait = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const getBackoffDelayMs = (attempt: number) => Math.pow(2, attempt) * 1000;
  const getHasValidFileForResolution = () => {
    if (typeResolutionSet === RESOLUTION_2275_2023) {
      return Boolean(resolution2275Document);
    }
    if (typeResolutionSet === RESOLUTION_3374_2000) {
      return Boolean(resolution3374Document);
    }
    return false;
  };

  const showRetryMessage = () => {
    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Carga fallida",
      description:
        "No fue posible cargar el archivo en este momento. Por favor, reintente nuevamente usando el mismo archivo (no es necesario volver a seleccionarlo).",
      onClose() {
        dispatch.messageModal.cleanStateMessage();
      },
    });
  };

  useEffect(() => {
    if (id && typeResolutionSet && getHasValidFileForResolution()) {
      postRips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resolution2275Document, resolution3374Document]);

  useEffect(() => {
    return () => {
      setResponseRips(null);
    };
  }, []);

  const handleValueCuv = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueCuv(e.target.value);
  };

  const postRips = async (attempt = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const hasValidFile = getHasValidFileForResolution();

    if (!hasValidFile || !id) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiInstance.post(
        `${GET_ELECTRONIC_DOCS}/${idDoc}/rips`,
        {
          attachmentId: id,
          typeResolution: typeResolutionSet,
          validationUniqueCode: valueCuv,
        },
      );
      setResponseRips(response);
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        await wait(getBackoffDelayMs(attempt));
        return postRips(attempt + 1);
      }
      showRetryMessage();
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFile = (file: any, typeResolution: string) => {
    setTypeResolutionSet(typeResolution);
    setIsLoading(true);
    if (
      file &&
      file.size < 5000000 &&
      file.name.split(".").pop() === "json" &&
      file.type === "application/json" &&
      typeResolution === RESOLUTION_2275_2023
    ) {
      setResolution2275Document(file);
      getLink(file, typeResolution);
    }
    if (
      file &&
      file.size < 5000000 &&
      file.name.split(".").pop() === "zip" &&
      typeResolution === RESOLUTION_3374_2000
    ) {
      setResolution3374Document(file);
      getLink(file, typeResolution);
    }
  };

  const getLink = async (
    file: any,
    typeResolution: string,
    attempt = 0,
  ): Promise<void> => {
    const MAX_RETRIES = 3;

    try {
      await dispatch.linkDocument.getLinkAsync({
        file: file,
        url: `${GET_ELECTRONIC_DOCS}/${idDoc}/rips/generate-upload-link`,
        typeResolution,
      });
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        await wait(getBackoffDelayMs(attempt));
        return getLink(file, typeResolution, attempt + 1);
      }
      showRetryMessage();
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnclickUpload = async () => {
    if (id) {
      if (responseRips?.status === 206) {
        if (resolution2275Document) {
          downloadDocument(
            responseRips,
            downloadsTitle.notificationRips,
            downloadsDescription.notificationRips,
            "success",
          );
          dispatch.linkDocument.cleanAllState();
          callGetDetailedDocument(idDoc || "");
          setIsNextPage(true);
        } else {
          downloadDocument(
            responseRips,
            downloadsTitle.errorRips,
            downloadsDescription.errorRips,
            "error",
          );
        }
      } else {
        dispatch.linkDocument.cleanAllState();
        callGetDetailedDocument(idDoc || "");
      }
    } else {
      Swal.fire({
        icon: "error",
        html: `Debe cargar archivo RIPS`,
        showCancelButton: false,
        showConfirmButton: true,
      });
    }
  };

  const removeFile = () => {
    setResponseRips(null);
    if (typeResolutionSet === RESOLUTION_2275_2023) {
      setResolution2275Document(null);
      dispatch.linkDocument.cleanStateNameNewsRips();
    }

    if (typeResolutionSet === RESOLUTION_3374_2000) {
      setResolution3374Document(null);
      dispatch.linkDocument.cleanState();
    }
  };

  return {
    id,
    originalName,
    billingState,
    resolution3374Document,
    resolution2275Document,
    isRipsValidate,
    isLoading,
    typeAccept,
    activityStatus,
    handleChangeFile,
    handleOnclickUpload,
    removeFile,
    setIsRipsValidate,
    handleValueCuv,
    nameNewsRips,
    valueCuv,
    typeAccept2275,
  };
};
